import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

import type { UpdateFMXGroupBody } from '../@types/fmxGroup';
import { makeAPIRequestV2 } from '../utils/api';

import { endpoints } from './endpoints';
import type { TLatestImagesResponse } from './useLatestImages';
import { usePreferences } from './usePreferences';

interface UseUpdateFMXGroup {
  visitId: number;
  updateFmxGroupBody: UpdateFMXGroupBody[];
}

export const useUpdateFMXGroup = () => {
  const { data: prefs } = usePreferences();
  const queryClient = useQueryClient();
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const queryKey = [
    endpoints.patient,
    endpoints.latestImages,
    selectedPatientId,
  ];

  const mutationFn = async (userModificationsData: UseUpdateFMXGroup) => {
    const { visitId, updateFmxGroupBody } = userModificationsData;

    if (prefs?.enableLatestImagesV2) {
      return makeAPIRequestV2(
        `/v2/${endpoints.patient}/${endpoints.visits}/${visitId}/${endpoints.fmxGroup}`,
        {
          // pass analysis ids instead of mongo image ids here
          data: updateFmxGroupBody.map((item) => ({
            ...item,
            imageId: item.analysisId,
          })),
        },
        'PATCH',
      );
    }
    return makeAPIRequestV2(
      `/${endpoints.patient}/${endpoints.visits}/${visitId}/${endpoints.fmxGroup}`,
      { data: updateFmxGroupBody },
      'PATCH',
    );
  };

  return useMutation(
    (userModificationsData: UseUpdateFMXGroup) => {
      return mutationFn(userModificationsData);
    },
    {
      onMutate: async (vars) => {
        const queryKeyPlusVisitId = [...queryKey, String(vars.visitId)];
        await queryClient.cancelQueries(queryKeyPlusVisitId, {
          exact: false,
        });

        // have to get the dig through the client to get the
        // query key because we don't have bw/the other attributes here.
        const allRelatedQueries = queryClient
          .getQueryCache()
          .findAll(queryKeyPlusVisitId);
        const actualQueryKey = allRelatedQueries?.[0]?.queryKey;

        actualQueryKey &&
          queryClient.setQueryData(
            actualQueryKey,
            (previousLatestImages: TLatestImagesResponse) => {
              if (!previousLatestImages) {
                return;
              }
              return {
                ...previousLatestImages,
                analyzedImages: previousLatestImages?.analyzedImages?.map(
                  (image) => {
                    const replaced = vars.updateFmxGroupBody.find(
                      (group) => group.imageId === image.imageId,
                    );
                    return {
                      ...image,
                      fmxIndex: replaced
                        ? replaced.newSlot || -1
                        : image.fmxIndex,
                      imageGroup: replaced
                        ? replaced.imageGroup
                        : image.imageGroup,
                    };
                  },
                ),
              };
            },
          );
        return { vars };
      },
      onSettled: async (_, __, variables) => {
        queryClient.invalidateQueries([...queryKey, variables.visitId], {
          exact: false,
        });
      },
    },
  );
};
